/**
 * The avatar pipeline.
 *
 * Expect a 403 today: role CUSTOMER is not granted `requestFileUpload` on
 * dev, so `GET /files/upload-request/` fails for every consumer. Treat
 * avatar upload as optional everywhere it appears.
 */

import AUTH_SERVICES from "../auth/authService";
import { ALLOWED_AVATAR_MIME_TYPES, isAllowedAvatarMime } from "../auth/authPolicy";

export class UnsupportedFileTypeError extends Error {
  constructor(mimeType) {
    super(`Unsupported file type: ${mimeType}`);
    this.name = "UnsupportedFileTypeError";
    this.mimeType = mimeType;
  }
}

export const ACCEPT_ATTRIBUTE = ALLOWED_AVATAR_MIME_TYPES.join(",");

const avatarFileName = (mimeType) => {
  const subtype = mimeType.split("/")[1] || "jpg";
  const ext = subtype === "jpeg" ? "jpg" : subtype;

  return `profile-${Date.now()}.${ext}`;
};

/**
 * Uploads a picked File and returns the durable `accessUrl`.
 *
 * Never persist `uploadUrl` — it is a presigned PUT valid 10 minutes.
 * `accessUrl` is the server reference to keep.
 *
 * @param {File} file
 * @returns {Promise<string>} accessUrl
 */
export const uploadImage = async (file) => {
  const mimeType = String(file?.type || "").toLowerCase();

  // `accept` on the input is a hint, not a guarantee.
  if (!isAllowedAvatarMime(mimeType)) throw new UnsupportedFileTypeError(mimeType);

  const { data } = await AUTH_SERVICES.getUploadRequest(avatarFileName(mimeType));
  const { uploadUrl, uploadMethod, accessUrl } = data || {};

  if (!uploadUrl || !accessUrl) throw new Error("Upload request returned no url");

  await AUTH_SERVICES.uploadFile(uploadUrl, uploadMethod ?? "PUT", file);

  return accessUrl;
};

/** Distinguishes the three failures the user can actually act on. */
export const uploadErrorMessage = (error) => {
  if (error instanceof UnsupportedFileTypeError) {
    return "Only PNG and JPEG photos are supported.";
  }

  const status = error?.response?.status;

  if (status === 403 || status === 401) {
    return "Photo uploads are not enabled for your account yet. You can add it later.";
  }

  return "Could not upload your photo. You can add it later.";
};
